// Prestige System - Permanent bonuses for completing the game

import { PRESTIGE_RATES, PRESTIGE_TIERS } from '../config/balance.js';

// Re-export balance constants for local use (destructured for cleaner code)
const {
    BASE_BONUS_RATE,
    EXTRA_BONUS_RATE,
    LEVEL_MULTIPLIER_INCREASE
} = PRESTIGE_RATES;

// Available skills for the selectable bonus
// Mile Earning is always granted, player chooses one of these for the second bonus
export const SELECTABLE_SKILLS = {
    baseSpeed: {
        id: 'baseSpeed',
        name: 'Idle Speed',
        description: 'Increases base driving speed',
        unit: '%'
    },
    acceleration: {
        id: 'acceleration',
        name: 'Acceleration',
        description: 'Increases speed gained per click',
        unit: '%'
    },
    maxSpeed: {
        id: 'maxSpeed',
        name: 'Max Speed',
        description: 'Increases maximum achievable speed',
        unit: '%'
    },
    cruiseDuration: {
        id: 'cruiseDuration',
        name: 'Autopilot Duration',
        description: 'Increases how long autopilot lasts',
        unit: '%'
    },
    cruiseCooldown: {
        id: 'cruiseCooldown',
        name: 'Autopilot Cooldown',
        description: 'Reduces autopilot cooldown time',
        unit: '%'
    },
    momentum: {
        id: 'momentum',
        name: 'Momentum Cap',
        description: 'Increases maximum momentum bonus',
        unit: '%'
    },
    momentumRate: {
        id: 'momentumRate',
        name: 'Momentum Rate',
        description: 'Increases how quickly momentum accumulates',
        unit: '%'
    }
};

/**
 * Calculate the level multiplier for a given prestige level
 * @param {number} level - Current prestige level (0 = never prestiged)
 * @returns {number} Multiplier (1.0, 1.25, 1.5, 1.75, etc.)
 */
export function getLevelMultiplier(level) {
    return 1 + (level * LEVEL_MULTIPLIER_INCREASE);
}

/**
 * Calculate prestige bonuses for a potential prestige
 * @param {number} totalMiles - Lifetime miles earned this run
 * @param {number} unspentMiles - Current available miles
 * @param {number} currentPrestigeLevel - Current prestige level before this prestige
 * @returns {object} Bonus breakdown
 */
export function calculatePrestigeBonus(totalMiles, unspentMiles, currentPrestigeLevel) {
    const levelMultiplier = getLevelMultiplier(currentPrestigeLevel);

    // Base bonus from total miles earned (applies to both mileEarning and selected skill)
    const baseBonus = totalMiles * BASE_BONUS_RATE * levelMultiplier;

    // Extra bonus from unspent miles (applies to both mileEarning and selected skill)
    const extraBonus = unspentMiles * EXTRA_BONUS_RATE * levelMultiplier;

    return {
        levelMultiplier,
        base: baseBonus,
        extra: extraBonus,
        total: baseBonus + extraBonus
    };
}

/**
 * Format a bonus percentage for display
 * @param {number} bonus - Bonus as decimal (0.15 = 15%)
 * @returns {string} Formatted string like "+15.5%"
 */
export function formatBonusPercent(bonus) {
    const percent = bonus * 100;
    if (percent >= 10) {
        return `+${percent.toFixed(1)}%`;
    } else if (percent >= 1) {
        return `+${percent.toFixed(2)}%`;
    } else {
        return `+${percent.toFixed(3)}%`;
    }
}

/**
 * Format a bonus as a multiplier for display (used for Mile Earning)
 * @param {number} bonus - Bonus as decimal (0.15 = 15% bonus = 1.15x multiplier)
 * @returns {string} Formatted string like "×1.15"
 */
export function formatBonusMultiplier(bonus) {
    const multiplier = 1 + bonus;
    return `×${multiplier.toFixed(2)}`;
}

/**
 * Get prestige tier name based on level
 * @param {number} level - Prestige level
 * @returns {string} Tier name
 */
export function getPrestigeTierName(level) {
    const { THRESHOLDS } = PRESTIGE_TIERS;
    if (level === 0) return null;
    if (level < THRESHOLDS.silver) return 'Bronze';
    if (level < THRESHOLDS.gold) return 'Silver';
    if (level < THRESHOLDS.platinum) return 'Gold';
    if (level < THRESHOLDS.diamond) return 'Platinum';
    if (level < THRESHOLDS.transcendent) return 'Diamond';
    return 'Transcendent';
}

/**
 * Get prestige tier color
 * @param {number} level - Prestige level
 * @returns {string} Hex color
 */
export function getPrestigeTierColor(level) {
    const { THRESHOLDS, COLORS } = PRESTIGE_TIERS;
    if (level === 0) return COLORS.none;
    if (level < THRESHOLDS.silver) return COLORS.bronze;
    if (level < THRESHOLDS.gold) return COLORS.silver;
    if (level < THRESHOLDS.platinum) return COLORS.gold;
    if (level < THRESHOLDS.diamond) return COLORS.platinum;
    if (level < THRESHOLDS.transcendent) return COLORS.diamond;
    return COLORS.transcendent;
}
